import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  PasswordInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import type { ApiKeyEntryDto } from '@domain/apiKey/ApiKeyDto'

/**
 * Overrides do plano de tenant para uma chave (ADR-007 D-1 e D-5).
 *
 * Só expõe os parâmetros que fazem sentido por cliente. Os demais continuam sendo decisão de
 * plataforma, na tela de Configurações — dar a cada tenant um painel completo transformaria a
 * política em algo negociável por linha, que não é o desenho.
 */

/** Vazio significa "usar o valor da plataforma", e é assim que se remove um override. */
const PLATFORM = ''

export function TenantSettingsModal({
  entry,
  onClose,
  onSaved,
  save,
}: {
  entry: ApiKeyEntryDto | null
  onClose: () => void
  onSaved: () => void
  save: (keyId: string, settings: Record<string, string>) => Promise<void>
}) {
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [destination, setDestination] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const settings = entry?.record.settings ?? {}
    // A chave vem mascarada; devolvê-la é seguro (o backend preserva a gravada ao ver `****`),
    // e mostrar a máscara é a única forma de o operador saber que existe uma chave ali.
    setApiKey(settings['llm.api-key'] ?? '')
    setModel(settings['llm.model'] ?? '')
    setBaseUrl(settings['llm.base-url'] ?? '')
    setDestination(settings['content.destination'] ?? null)
    setError(null)
  }, [entry])

  const submit = async () => {
    if (!entry) return
    setSaving(true)
    setError(null)
    try {
      const settings: Record<string, string> = {}
      if (apiKey.trim()) settings['llm.api-key'] = apiKey.trim()
      if (model.trim()) settings['llm.model'] = model.trim()
      if (baseUrl.trim()) settings['llm.base-url'] = baseUrl.trim()
      if (destination) settings['content.destination'] = destination
      await save(entry.key_id, settings)
      onSaved()
    } catch (err: any) {
      setError(messageFor(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      opened={entry !== null}
      onClose={onClose}
      title={entry ? `Configurações do tenant ${entry.record.tenant_id}` : ''}
      size="lg"
    >
      {entry && (
        <Stack gap="md">
          <Alert color="blue" variant="light">
            Estes valores sobrepõem os da plataforma só para este tenant. Campo vazio significa
            usar o da plataforma — é assim que se remove um override.
          </Alert>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          <Divider label="Credencial de LLM (BYOK)" labelPosition="left" />

          <PasswordInput
            label="Chave do tenant"
            description={
              entry.record.settings?.['llm.api-key']
                ? 'Já existe uma chave gravada. Deixe como está para mantê-la, ou digite outra para substituir.'
                : 'Sem chave própria, este tenant usa a chave de plataforma. Depois de gravada não há como lê-la de volta.'
            }
            placeholder="sk-or-v1-..."
            value={apiKey}
            onChange={(e) => setApiKey(e.currentTarget.value)}
          />

          <TextInput
            label="Modelo"
            description="Vazio usa o modelo da plataforma."
            placeholder="gpt-4o-mini"
            value={model}
            onChange={(e) => setModel(e.currentTarget.value)}
          />

          <TextInput
            label="Endpoint"
            description="Vazio usa o padrão do provider. Para OpenRouter: https://openrouter.ai/api/v1"
            placeholder="https://openrouter.ai/api/v1"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.currentTarget.value)}
          />

          <Divider label="Destino do conteúdo" labelPosition="left" />

          <Select
            label="Para onde o conteúdo deste tenant pode ir"
            description="Vazio usa a política da plataforma."
            data={[
              { value: 'local', label: 'local — nada sai da nossa infra' },
              { value: 'external_zdr', label: 'external_zdr — API externa com zero retenção' },
              { value: 'external', label: 'external — API externa sem restrição' },
            ]}
            value={destination}
            onChange={setDestination}
            clearable
          />

          <Text size="xs" c="dimmed">
            ZDR limita a <b>retenção</b> pelo provedor, não a <b>transferência</b>: no nível
            external_zdr o conteúdo ainda sai do país e é processado no exterior — o que se ganha é
            que ninguém o guarda depois. A base legal da transferência é contratual. Se a
            plataforma travou este parâmetro no ambiente, o que for escolhido aqui não terá efeito.
          </Text>

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={submit} loading={saving}>
              Gravar
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  )
}

function messageFor(err: any): string {
  const status = err?.response?.status ?? err?.status
  const detail = err?.response?.data?.error ?? err?.message
  if (status === 503) {
    return (
      detail ||
      'Falta a chave de cifra (DOCLENZ_SECRETS_KEY) no servidor. A credencial não é gravada sem ' +
        'ela — de propósito, para não ficar em texto puro no Redis.'
    )
  }
  if (status === 400) return detail || 'Valor inválido.'
  return detail || 'Não foi possível gravar.'
}
